import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { 
  BookOpen, 
  Users, 
  DollarSign, 
  FileText, 
  Settings, 
  HelpCircle,
  Download,
  CreditCard,
  MessageSquare,
  User,
  BarChart3,
  Eye,
  Plus,
  Edit,
  Search
} from 'lucide-react';

const Documentation: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-6xl mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-blue-600 mb-4">
            Administrator Documentation
          </h1>
          <p className="text-xl text-gray-600 mb-2">
            Complete Admin Guide for Royalty Management System
          </p>
          <Badge variant="outline" className="text-sm">
            Version 1.0 - Admin Only
          </Badge>
        </div>

        {/* Table of Contents */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BookOpen className="h-5 w-5" />
              Admin Guide Contents
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <h4 className="font-semibold mb-2 text-blue-600">Core Functions</h4>
                <ul className="space-y-1 text-sm">
                  <li><a href="#admin-overview" className="hover:underline">• Admin Overview</a></li>
                  <li><a href="#admin-dashboard" className="hover:underline">• Dashboard Management</a></li>
                  <li><a href="#manage-authors" className="hover:underline">• Managing Authors</a></li>
                  <li><a href="#manage-books" className="hover:underline">• Managing Books</a></li>
                </ul>
              </div>
              <div>
                <h4 className="font-semibold mb-2 text-blue-600">Advanced Features</h4>
                <ul className="space-y-1 text-sm">
                  <li><a href="#manage-royalties" className="hover:underline">• Royalty Reports</a></li>
                  <li><a href="#manage-payments" className="hover:underline">• Payment Processing</a></li>
                  <li><a href="#support-tickets" className="hover:underline">• Support Tickets</a></li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Admin Documentation */}
        <div className="mb-12">
          {/* Admin Overview */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle id="admin-overview" className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                Admin Overview
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="mb-4">
                As an administrator, you have full access to manage authors, books, royalty reports, 
                payments, and support tickets. The admin panel provides comprehensive tools to oversee 
                the entire royalty management system.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-4 bg-blue-50 rounded-lg">
                  <h4 className="font-semibold mb-2">Key Responsibilities</h4>
                  <ul className="text-sm space-y-1">
                    <li>• Manage author accounts and profiles</li>
                    <li>• Create and assign books to authors</li>
                    <li>• Generate royalty reports</li>
                    <li>• Process payment requests</li>
                    <li>• Handle support tickets</li>
                  </ul>
                </div>
                <div className="p-4 bg-green-50 rounded-lg">
                  <h4 className="font-semibold mb-2">Access Credentials</h4>
                  <p className="text-sm mb-2">Demo Admin Login:</p>
                  <p className="text-sm font-mono">Email: admin@example.com</p>
                  <p className="text-sm font-mono">Password: Welcome2025!</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Admin Dashboard */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle id="admin-dashboard" className="flex items-center gap-2">
                <BarChart3 className="h-5 w-5" />
                Dashboard Management
              </CardTitle>
            </CardHeader>
            <CardContent>
              <h4 className="font-semibold mb-3">Step-by-Step Guide:</h4>
              <div className="space-y-4">
                <div className="flex gap-3">
                  <div className="bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">1</div>
                  <div>
                    <p className="font-medium">Access the Dashboard</p>
                    <p className="text-sm text-gray-600">Navigate to <code>/admin/dashboard</code> after logging in</p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <div className="bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">2</div>
                  <div>
                    <p className="font-medium">Review Key Metrics</p>
                    <p className="text-sm text-gray-600">Monitor total authors, royalties, pending payments, and open tickets</p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <div className="bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">3</div>
                  <div>
                    <p className="font-medium">Analyze Trends</p>
                    <p className="text-sm text-gray-600">Use the royalty trends chart to track quarterly performance</p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <div className="bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">4</div>
                  <div>
                    <p className="font-medium">Take Quick Actions</p>
                    <p className="text-sm text-gray-600">Click on any metric card to navigate to the relevant section</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Managing Authors */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle id="manage-authors" className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                Managing Authors
              </CardTitle>
            </CardHeader>
            <CardContent>
              <h4 className="font-semibold mb-3">Adding New Authors:</h4>
              <div className="space-y-3 mb-6">
                <div className="flex gap-3">
                  <div className="bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">1</div>
                  <div>
                    <p className="font-medium">Navigate to Authors Section</p>
                    <p className="text-sm text-gray-600">Go to <code>/admin/authors</code></p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <div className="bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">2</div>
                  <div>
                    <p className="font-medium">Click "Add Author"</p>
                    <p className="text-sm text-gray-600">Use the <Plus className="h-4 w-4 inline" /> Add Author button</p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <div className="bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">3</div>
                  <div>
                    <p className="font-medium">Fill Author Information</p>
                    <p className="text-sm text-gray-600">Enter name, email, and other required details</p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <div className="bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">4</div>
                  <div>
                    <p className="font-medium">Save and Notify</p>
                    <p className="text-sm text-gray-600">The author will receive login credentials via email</p>
                  </div>
                </div>
              </div>

              <h4 className="font-semibold mb-3">Managing Existing Authors:</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="p-3 border rounded-lg">
                  <Eye className="h-5 w-5 mb-2 text-blue-600" />
                  <p className="font-medium text-sm">View Profile</p>
                  <p className="text-xs text-gray-600">See author details, balance, and books</p>
                </div>
                <div className="p-3 border rounded-lg">
                  <BookOpen className="h-5 w-5 mb-2 text-green-600" />
                  <p className="font-medium text-sm">Manage Books</p>
                  <p className="text-xs text-gray-600">Add or view author's books</p>
                </div>
                <div className="p-3 border rounded-lg">
                  <DollarSign className="h-5 w-5 mb-2 text-yellow-600" />
                  <p className="font-medium text-sm">View Royalties</p>
                  <p className="text-xs text-gray-600">Check royalty reports and payments</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Managing Books */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle id="manage-books" className="flex items-center gap-2">
                <BookOpen className="h-5 w-5" />
                Managing Books
              </CardTitle>
            </CardHeader>
            <CardContent>
              <h4 className="font-semibold mb-3">Adding Books:</h4>
              <div className="space-y-3 mb-6">
                <div className="flex gap-3">
                  <div className="bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">1</div>
                  <div>
                    <p className="font-medium">Access Books Section</p>
                    <p className="text-sm text-gray-600">Go to <code>/admin/books</code> or through author profile</p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <div className="bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">2</div>
                  <div>
                    <p className="font-medium">Click "Add Book"</p>
                    <p className="text-sm text-gray-600">Use the add book button in the interface</p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <div className="bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">3</div>
                  <div>
                    <p className="font-medium">Enter Book Details</p>
                    <p className="text-sm text-gray-600">Title, ISBN, publish date, and assign to author</p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <div className="bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">4</div>
                  <div>
                    <p className="font-medium">Save and Confirm</p>
                    <p className="text-sm text-gray-600">Book will be linked to the selected author</p>
                  </div>
                </div>
              </div>

              <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <h4 className="font-semibold mb-2 text-yellow-800">Important Notes:</h4>
                <ul className="text-sm text-yellow-700 space-y-1">
                  <li>• Each book must have a unique ISBN</li>
                  <li>• Books can only be assigned to one author</li>
                  <li>• Use the search feature to find existing books quickly</li>
                </ul>
              </div>
            </CardContent>
          </Card>

          {/* Managing Royalties */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle id="manage-royalties" className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Royalty Reports Management
              </CardTitle>
            </CardHeader>
            <CardContent>
              <h4 className="font-semibold mb-3">Creating Royalty Reports:</h4>
              <div className="space-y-3 mb-6">
                <div className="flex gap-3">
                  <div className="bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">1</div>
                  <div>
                    <p className="font-medium">Navigate to Royalties</p>
                    <p className="text-sm text-gray-600">Go to <code>/admin/royalties</code></p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <div className="bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">2</div>
                  <div>
                    <p className="font-medium">Click "Create Report"</p>
                    <p className="text-sm text-gray-600">Use the create new report button</p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <div className="bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">3</div>
                  <div>
                    <p className="font-medium">Select Parameters</p>
                    <p className="text-sm text-gray-600">Choose author, book, quarter, year, and sales data</p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <div className="bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">4</div>
                  <div>
                    <p className="font-medium">Review and Save</p>
                    <p className="text-sm text-gray-600">Verify all information before creating the report</p>
                  </div>
                </div>
              </div>

              <h4 className="font-semibold mb-3">Managing Existing Reports:</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-3 border rounded-lg">
                  <Eye className="h-5 w-5 mb-2 text-blue-600" />
                  <p className="font-medium text-sm">View Reports</p>
                  <p className="text-xs text-gray-600">Click on any report to see detailed breakdown</p>
                </div>
                <div className="p-3 border rounded-lg">
                  <Edit className="h-5 w-5 mb-2 text-yellow-600" />
                  <p className="font-medium text-sm">Edit Reports</p>
                  <p className="text-xs text-gray-600">Modify sales data, amounts, or status</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Payment Processing */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle id="manage-payments" className="flex items-center gap-2">
                <CreditCard className="h-5 w-5" />
                Payment Processing
              </CardTitle>
            </CardHeader>
            <CardContent>
              <h4 className="font-semibold mb-3">Processing Payment Requests:</h4>
              <div className="space-y-3 mb-6">
                <div className="flex gap-3">
                  <div className="bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">1</div>
                  <div>
                    <p className="font-medium">Review Pending Requests</p>
                    <p className="text-sm text-gray-600">Go to <code>/admin/payments</code> to see all requests</p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <div className="bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">2</div>
                  <div>
                    <p className="font-medium">Verify Payment Details</p>
                    <p className="text-sm text-gray-600">Check author's payment method and requested amount</p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <div className="bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">3</div>
                  <div>
                    <p className="font-medium">Process or Reject</p>
                    <p className="text-sm text-gray-600">Approve payment or reject with reason</p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <div className="bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">4</div>
                  <div>
                    <p className="font-medium">Update Status</p>
                    <p className="text-sm text-gray-600">Mark as completed once payment is sent</p>
                  </div>
                </div>
              </div>

              <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                <h4 className="font-semibold mb-2 text-red-800">Payment Processing Guidelines:</h4>
                <ul className="text-sm text-red-700 space-y-1">
                  <li>• Verify author has sufficient balance</li>
                  <li>• Check payment method is valid and complete</li>
                  <li>• Process payments within 3-5 business days</li>
                  <li>• Always provide clear rejection reasons</li>
                </ul>
              </div>
            </CardContent>
          </Card>

          {/* Support Tickets */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle id="support-tickets" className="flex items-center gap-2">
                <MessageSquare className="h-5 w-5" />
                Support Ticket Management
              </CardTitle>
            </CardHeader>
            <CardContent>
              <h4 className="font-semibold mb-3">Handling Support Tickets:</h4>
              <div className="space-y-3 mb-6">
                <div className="flex gap-3">
                  <div className="bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">1</div>
                  <div>
                    <p className="font-medium">Access Support Section</p>
                    <p className="text-sm text-gray-600">Go to <code>/admin/support</code></p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <div className="bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">2</div>
                  <div>
                    <p className="font-medium">Review Open Tickets</p>
                    <p className="text-sm text-gray-600">Prioritize by urgency and creation date</p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <div className="bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">3</div>
                  <div>
                    <p className="font-medium">Respond to Authors</p>
                    <p className="text-sm text-gray-600">Provide clear, helpful responses</p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <div className="bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">4</div>
                  <div>
                    <p className="font-medium">Update Status</p>
                    <p className="text-sm text-gray-600">Mark as in progress or resolved</p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="p-3 border rounded-lg">
                  <Badge variant="outline" className="mb-2 bg-yellow-50">Open</Badge>
                  <p className="text-sm">New tickets requiring attention</p>
                </div>
                <div className="p-3 border rounded-lg">
                  <Badge variant="outline" className="mb-2 bg-blue-50">In Progress</Badge>
                  <p className="text-sm">Tickets being actively worked on</p>
                </div>
                <div className="p-3 border rounded-lg">
                  <Badge variant="outline" className="mb-2 bg-green-50">Resolved</Badge>
                  <p className="text-sm">Completed tickets</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <Separator className="my-12" />

        {/* Author Documentation */}
        <div className="mb-12">
          <h2 id="author-overview" className="text-3xl font-bold text-green-600 mb-6">
            Author Guide
          </h2>

          {/* Author Overview */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="h-5 w-5" />
                Author Overview
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="mb-4">
                As an author, you can view your royalty reports, request payments, manage your 
                payment preferences, and get support when needed. The author portal provides 
                all the tools you need to track your earnings and manage your account.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-4 bg-green-50 rounded-lg">
                  <h4 className="font-semibold mb-2">What You Can Do</h4>
                  <ul className="text-sm space-y-1">
                    <li>• View quarterly royalty reports</li>
                    <li>• Download PDF reports</li>
                    <li>• Request payments when eligible</li>
                    <li>• Set up payment preferences</li>
                    <li>• Submit support tickets</li>
                    <li>• Update account settings</li>
                  </ul>
                </div>
                <div className="p-4 bg-blue-50 rounded-lg">
                  <h4 className="font-semibold mb-2">Demo Access</h4>
                  <p className="text-sm mb-2">Demo Author Login:</p>
                  <p className="text-sm font-mono">Email: author@example.com</p>
                  <p className="text-sm font-mono">Password: author111</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Author Dashboard */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle id="author-dashboard" className="flex items-center gap-2">
                <BarChart3 className="h-5 w-5" />
                Author Dashboard
              </CardTitle>
            </CardHeader>
            <CardContent>
              <h4 className="font-semibold mb-3">Understanding Your Dashboard:</h4>
              <div className="space-y-4">
                <div className="flex gap-3">
                  <div className="bg-green-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">1</div>
                  <div>
                    <p className="font-medium">Current Balance</p>
                    <p className="text-sm text-gray-600">Your available earnings ready for payment</p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <div className="bg-green-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">2</div>
                  <div>
                    <p className="font-medium">Recent Royalty Reports</p>
                    <p className="text-sm text-gray-600">Latest quarterly earnings breakdown</p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <div className="bg-green-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">3</div>
                  <div>
                    <p className="font-medium">Payment Eligibility</p>
                    <p className="text-sm text-gray-600">Check if you can request a payment</p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <div className="bg-green-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">4</div>
                  <div>
                    <p className="font-medium">Quick Actions</p>
                    <p className="text-sm text-gray-600">Access key features like payments and support</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Viewing Royalties */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle id="view-royalties" className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Viewing Royalty Reports
              </CardTitle>
            </CardHeader>
            <CardContent>
              <h4 className="font-semibold mb-3">Accessing Your Reports:</h4>
              <div className="space-y-3 mb-6">
                <div className="flex gap-3">
                  <div className="bg-green-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">1</div>
                  <div>
                    <p className="font-medium">Navigate to Royalty Reports</p>
                    <p className="text-sm text-gray-600">Go to <code>/author/royalties</code> or use sidebar menu</p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <div className="bg-green-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">2</div>
                  <div>
                    <p className="font-medium">Browse Available Reports</p>
                    <p className="text-sm text-gray-600">See all your quarterly reports by year and quarter</p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <div className="bg-green-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">3</div>
                  <div>
                    <p className="font-medium">View Report Details</p>
                    <p className="text-sm text-gray-600">Click on any report to see monthly breakdown</p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <div className="bg-green-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">4</div>
                  <div>
                    <p className="font-medium">Download PDF</p>
                    <p className="text-sm text-gray-600">Use the <Download className="h-4 w-4 inline" /> download button for PDF copy</p>
                  </div>
                </div>
              </div>

              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <h4 className="font-semibold mb-2 text-blue-800">Report Information Includes:</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-blue-700">
                  <ul className="space-y-1">
                    <li>• Book title and ISBN</li>
                    <li>• Report period (quarter/year)</li>
                    <li>• Total units sold</li>
                  </ul>
                  <ul className="space-y-1">
                    <li>• Monthly sales breakdown</li>
                    <li>• Gross and net amounts</li>
                    <li>• Current status</li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Requesting Payments */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle id="request-payments" className="flex items-center gap-2">
                <CreditCard className="h-5 w-5" />
                Requesting Payments
              </CardTitle>
            </CardHeader>
            <CardContent>
              <h4 className="font-semibold mb-3">How to Request a Payment:</h4>
              <div className="space-y-3 mb-6">
                <div className="flex gap-3">
                  <div className="bg-green-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">1</div>
                  <div>
                    <p className="font-medium">Check Eligibility</p>
                    <p className="text-sm text-gray-600">Ensure you meet the minimum payment threshold</p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <div className="bg-green-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">2</div>
                  <div>
                    <p className="font-medium">Go to Payments</p>
                    <p className="text-sm text-gray-600">Navigate to <code>/author/payments</code></p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <div className="bg-green-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">3</div>
                  <div>
                    <p className="font-medium">Click "Request Payment"</p>
                    <p className="text-sm text-gray-600">Use the request payment button if eligible</p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <div className="bg-green-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">4</div>
                  <div>
                    <p className="font-medium">Enter Amount</p>
                    <p className="text-sm text-gray-600">Specify how much you want to withdraw (up to your balance)</p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <div className="bg-green-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">5</div>
                  <div>
                    <p className="font-medium">Submit Request</p>
                    <p className="text-sm text-gray-600">Review and submit for admin approval</p>
                  </div>
                </div>
              </div>

              <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <h4 className="font-semibold mb-2 text-yellow-800">Payment Requirements:</h4>
                <ul className="text-sm text-yellow-700 space-y-1">
                  <li>• Must have payment method set up</li>
                  <li>• Balance must meet minimum threshold</li>
                  <li>• Cannot have pending payment requests</li>
                  <li>• Processing takes 3-5 business days</li>
                </ul>
              </div>
            </CardContent>
          </Card>

          {/* Payment Preferences */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle id="payment-preferences" className="flex items-center gap-2">
                <Settings className="h-5 w-5" />
                Payment Preferences
              </CardTitle>
            </CardHeader>
            <CardContent>
              <h4 className="font-semibold mb-3">Setting Up Payment Methods:</h4>
              <div className="space-y-3 mb-6">
                <div className="flex gap-3">
                  <div className="bg-green-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">1</div>
                  <div>
                    <p className="font-medium">Access Payment Preferences</p>
                    <p className="text-sm text-gray-600">Go to <code>/author/payment-preferences</code></p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <div className="bg-green-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">2</div>
                  <div>
                    <p className="font-medium">Choose Payment Method</p>
                    <p className="text-sm text-gray-600">Select from PayPal, bank transfer, or check</p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <div className="bg-green-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">3</div>
                  <div>
                    <p className="font-medium">Enter Details</p>
                    <p className="text-sm text-gray-600">Provide required information for your chosen method</p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <div className="bg-green-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">4</div>
                  <div>
                    <p className="font-medium">Set Threshold</p>
                    <p className="text-sm text-gray-600">Choose minimum amount for automatic payments</p>
                  </div>
                </div>
              </div>

              <h4 className="font-semibold mb-3">Available Payment Methods:</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-3 border rounded-lg">
                  <h5 className="font-medium mb-2">PayPal</h5>
                  <p className="text-sm text-gray-600">Fast, secure payments to your PayPal account</p>
                  <p className="text-xs text-gray-500 mt-1">Required: PayPal email address</p>
                </div>
                <div className="p-3 border rounded-lg">
                  <h5 className="font-medium mb-2">Bank Transfer</h5>
                  <p className="text-sm text-gray-600">Direct deposit to your bank account</p>
                  <p className="text-xs text-gray-500 mt-1">Required: Account & routing numbers</p>
                </div>
                <div className="p-3 border rounded-lg">
                  <h5 className="font-medium mb-2">International Transfer</h5>
                  <p className="text-sm text-gray-600">Wire transfer for international authors</p>
                  <p className="text-xs text-gray-500 mt-1">Required: SWIFT code, bank details</p>
                </div>
                <div className="p-3 border rounded-lg">
                  <h5 className="font-medium mb-2">Check</h5>
                  <p className="text-sm text-gray-600">Physical check mailed to your address</p>
                  <p className="text-xs text-gray-500 mt-1">Required: Mailing address</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Getting Support */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle id="get-support" className="flex items-center gap-2">
                <HelpCircle className="h-5 w-5" />
                Getting Support
              </CardTitle>
            </CardHeader>
            <CardContent>
              <h4 className="font-semibold mb-3">Submitting Support Tickets:</h4>
              <div className="space-y-3 mb-6">
                <div className="flex gap-3">
                  <div className="bg-green-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">1</div>
                  <div>
                    <p className="font-medium">Access Help Center</p>
                    <p className="text-sm text-gray-600">Go to <code>/author/help</code></p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <div className="bg-green-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">2</div>
                  <div>
                    <p className="font-medium">Check FAQs First</p>
                    <p className="text-sm text-gray-600">Browse common questions for quick answers</p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <div className="bg-green-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">3</div>
                  <div>
                    <p className="font-medium">Submit a Ticket</p>
                    <p className="text-sm text-gray-600">If you can't find an answer, create a support ticket</p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <div className="bg-green-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">4</div>
                  <div>
                    <p className="font-medium">Track Your Tickets</p>
                    <p className="text-sm text-gray-600">Monitor ticket status at <code>/author/tickets</code></p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-4 bg-blue-50 rounded-lg">
                  <h4 className="font-semibold mb-2">When to Contact Support</h4>
                  <ul className="text-sm space-y-1">
                    <li>• Payment issues or delays</li>
                    <li>• Royalty report discrepancies</li>
                    <li>• Account access problems</li>
                    <li>• Technical issues</li>
                  </ul>
                </div>
                <div className="p-4 bg-green-50 rounded-lg">
                  <h4 className="font-semibold mb-2">Response Times</h4>
                  <ul className="text-sm space-y-1">
                    <li>• General inquiries: 24-48 hours</li>
                    <li>• Payment issues: 12-24 hours</li>
                    <li>• Technical problems: 4-8 hours</li>
                    <li>• Urgent matters: 2-4 hours</li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Account Settings */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle id="account-settings" className="flex items-center gap-2">
                <User className="h-5 w-5" />
                Account Settings
              </CardTitle>
            </CardHeader>
            <CardContent>
              <h4 className="font-semibold mb-3">Managing Your Account:</h4>
              <div className="space-y-3 mb-6">
                <div className="flex gap-3">
                  <div className="bg-green-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">1</div>
                  <div>
                    <p className="font-medium">Access Account Settings</p>
                    <p className="text-sm text-gray-600">Go to <code>/author/account</code></p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <div className="bg-green-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">2</div>
                  <div>
                    <p className="font-medium">Update Profile Information</p>
                    <p className="text-sm text-gray-600">Change your name, email, or other details</p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <div className="bg-green-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">3</div>
                  <div>
                    <p className="font-medium">Change Password</p>
                    <p className="text-sm text-gray-600">Update your password for security</p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <div className="bg-green-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">4</div>
                  <div>
                    <p className="font-medium">Upload Avatar</p>
                    <p className="text-sm text-gray-600">Add a profile picture to personalize your account</p>
                  </div>
                </div>
              </div>

              <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                <h4 className="font-semibold mb-2 text-red-800">Important Security Notes:</h4>
                <ul className="text-sm text-red-700 space-y-1">
                  <li>• Always use a strong, unique password</li>
                  <li>• Never share your login credentials</li>
                  <li>• Log out when using shared computers</li>
                  <li>• Contact support immediately if you suspect unauthorized access</li>
                </ul>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Footer */}
        <div className="text-center py-8 border-t">
          <p className="text-gray-600 mb-2">
            For additional support, contact us at{' '}
            <a href="mailto:support@authorstranquilitypress.com" className="text-blue-600 hover:underline">
              support@authorstranquilitypress.com
            </a>
          </p>
          <p className="text-gray-600">
            Or call our 24/7 hotline: <span className="font-medium">+1 (866) 411 8655</span>
          </p>
          <p className="text-sm text-gray-500 mt-4">
            © 2025 Author's Tranquility Press - Admin Documentation | Powered by Kat =)
          </p>
        </div>
      </div>
    </div>
  );
};

export default Documentation;
